import {
  IsString,
  IsNotEmpty,
  IsOptional,
  IsArray,
  IsBoolean,
  IsNumber,
  ValidateIf,
  ArrayUnique,
  IsInt,
} from 'class-validator';
import { Transform } from 'class-transformer';
import { MaxContentSize } from '../validators/content-size.validator';
import { SiteSettings } from './site-settings-types';
import type { CropData } from './site-settings-types';
import { Asset } from './asset-types';

// Post status enum
export enum PostStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  SCHEDULED = 'scheduled',
  ARCHIVED = 'archived',
  UPDATE_DRAFT = 'update_draft',
  GENERATING_DRAFT = 'generating_draft',
  GENERATED_DRAFT = 'generated_draft',
  GENERATION_ERROR = 'generation_error',
}

// Post summary for list views (lightweight)
export interface PostSummary {
  id: string;
  title: string;
  subtitle?: string;
  author?: string;
  isPublished: boolean; // Whether published version exists
  hasDraft: boolean; // Whether draft changes exist
  featured: boolean;
  postTypes: string[];
  topics: string[];
  publishDate?: string;
  archiveDate?: string;
  createdAt: string;
  updatedAt: string;
  siteId: string;
  attachmentCount?: number;
  generationStatus?: 'generating' | 'completed' | 'error';
  generationError?: string;
}

// Post content (used in both draft and published)
export interface PostContent {
  title: string;
  subtitle?: string;
  author?: string;
  bannerImage?: Asset;
  bannerImageCrop?: CropData;
  featuredVideo?: Asset;
  content: string;
  featured: boolean;
  postTypes: string[];
  topics: string[];
}

// Published post data (content + metadata)
export interface PublishedPostData extends PostContent {
  publishedAt: string;
}

// Full Post interface with draft/published structure
// Admin sees both draft and published
// Viewers see this too but with draft always null
export interface Post {
  id: string;
  siteId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;

  // Top-level metadata
  publishDate?: string | null;
  archiveDate?: string | null;
  restrictedAccess?: boolean;
  allowedInfluencerIds?: number[];

  // Content versions
  published: PublishedPostData | null;
  draft: PostContent | null;

  // AI generation tracking
  generationStatus?: 'generating' | 'completed' | 'error';
  generationError?: string;
}

// Post attachment interface (separate collection)
export interface Attachment {
  id: string;
  postId?: string; // Optional when attached to draft
  draftId?: string; // Optional when attached to post
  siteId: string;
  assetId: string;
  asset?: Asset; // Hydrated from assets collection
  description?: string;
  displayName?: string;
  order: number;
  createdAt: string;
  createdBy: string;
}

// Comment interface
export interface Comment {
  id: string;
  postId: string;
  siteId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  deletedAt?: string;
}

// User roles enum - matches Identity Server arconnect_role claim values
export enum Role {
  NONE = '',
  ADMIN = 'Admin',
  VIEWER = 'Viewer',
}

// User data interface
export interface UserData {
  userId: string;
  name: string;
  email: string;
  role: Role; // ARConnect role from Identity Server (ADMIN, VIEWER, or NONE)
  siteIds: string[]; // Array of site IDs the user has access to
  currentSiteId?: string; // Currently selected site
  membershipUserId?: string; // Identity server user ID
  analystId?: number; // ARchitect analyst ID (for Matomo tracking)
  firmId?: number; // Firm ID (for Matomo tracking)
  intercomHMAC?: string; // Intercom user hash for secure identity verification
}

// Domain event names
export enum DomainEventName {
  POST_CREATED = 'post.created',
  POST_UPDATED = 'post.updated',
  POST_DELETED = 'post.deleted',
  POST_GENERATION_REQUESTED = 'post.generation.requested',
  POST_GENERATION_COMPLETED = 'post.generation.completed',
  POST_GENERATION_FAILED = 'post.generation.failed',
  ASSET_CREATED = 'asset.created',
  ASSET_UPDATED = 'asset.updated',
  ASSET_DELETED = 'asset.deleted',
  SITE_SETTINGS_CREATED = 'site.settings.created',
  SITE_SETTINGS_UPDATED = 'site.settings.updated',
  ATTACHMENT_CREATED = 'attachment.created',
  ATTACHMENT_UPDATED = 'attachment.updated',
  ATTACHMENT_DELETED = 'attachment.deleted',
  ANALYST_ACCESS_STATUS_CHANGED = 'analyst.access.status.changed',
}

// Base domain event interface
export interface BaseDomainEvent {
  user?: UserData; // Optional for system-initiated operations
  changesDescription: string;
}

// Domain event interfaces
export interface PostCreatedEvent extends BaseDomainEvent {
  post: Post;
}

export interface PostUpdatedEvent extends BaseDomainEvent {
  post: Post;
  previousPost: Post;
}

export interface PostDeletedEvent extends BaseDomainEvent {
  postId: string;
  siteId: string;
  deletedPost: Post;
}

// Site settings event interfaces
export interface SiteSettingsCreatedEvent extends BaseDomainEvent {
  siteSettings: SiteSettings;
}

export interface SiteSettingsUpdatedEvent extends BaseDomainEvent {
  siteSettings: SiteSettings;
  previousSiteSettings: SiteSettings;
}

// Attachment event interfaces
export interface AttachmentCreatedEvent extends BaseDomainEvent {
  attachment: Attachment;
}

export interface AttachmentUpdatedEvent extends BaseDomainEvent {
  attachment: Attachment;
  previousAttachment: Attachment;
}

export interface AttachmentDeletedEvent extends BaseDomainEvent {
  attachmentId: string;
  siteId: string;
  deletedAttachment: Attachment;
}

// Post generation event interfaces
export interface PostGenerationRequestedEvent {
  postId: string;
  siteId: string;
  source: 'url' | 'document';
  extractedContent: {
    title?: string;
    content?: string;
    images?: Array<{
      url?: string;
      altText?: string;
      buffer?: Buffer;
      filename?: string;
    }>;
    videos?: Array<{ url: string; title?: string }>;
  };
  user?: UserData;
}

export interface PostGenerationCompletedEvent extends BaseDomainEvent {
  postId: string;
  siteId: string;
}

export interface PostGenerationFailedEvent extends BaseDomainEvent {
  postId: string;
  siteId: string;
  error: string;
}

// Audit entry interface
export interface AuditEntry {
  auditId: string; // UUID (PK)
  timestamp: string; // ISO string (SK)
  entityType: 'post' | 'asset' | 'site-settings' | 'analyst-site-access';
  entityId: string; // UUID - same value as GSI1PK
  action: 'created' | 'updated' | 'deleted';
  userId?: string;
  userName?: string;
  changesDescription?: string; // Simple string description of changes
  metadata: {
    siteId?: string;
  };
}

// Consolidated Post Event (replaces PostCreatedEvent, PostUpdatedEvent, PostDeletedEvent)
export interface PostEvent {
  type: 'created' | 'updated' | 'deleted';
  post: Post;
  previousPost?: Post; // Only for updates
  postId?: string; // Only for deletes (when post is deleted)
  siteId?: string; // Only for deletes (when post is deleted)
}

// Post DTOs for API operations (database format with asset IDs instead of full Asset objects)
export class CreatePostDto {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsOptional()
  @IsString()
  subtitle?: string;

  @IsOptional()
  @IsString()
  author?: string;

  @IsOptional()
  @IsString()
  bannerImageAssetId?: string;

  @IsOptional()
  bannerImageCrop?: CropData;

  @IsOptional()
  @ValidateIf((o) => o.featuredVideoAssetId !== null)
  @IsString()
  featuredVideoAssetId?: string | null;

  @IsString()
  @MaxContentSize()
  content: string;

  @IsArray()
  @IsString({ each: true })
  postTypes: string[];

  @IsArray()
  @IsString({ each: true })
  topics: string[];

  @IsOptional()
  @IsString()
  publishDate?: string;

  @IsOptional()
  @IsString()
  archiveDate?: string;

  @IsOptional()
  @IsBoolean()
  restrictedAccess?: boolean;

  @Transform(
    ({ value }) => {
      if (!Array.isArray(value)) return value;
      return value.map((id) => {
        const parsed =
          typeof id === 'string' ? parseInt(id.trim(), 10) : Number(id);
        return isNaN(parsed) ? id : parsed;
      });
    },
    { toClassOnly: true }
  )
  @IsOptional()
  @IsArray({
    message: 'allowedInfluencerIds must be an array',
  })
  @ArrayUnique({ message: 'allowedInfluencerIds must contain unique values' })
  @IsInt({ each: true, message: 'Each allowedInfluencerId must be an integer' })
  allowedInfluencerIds?: number[];

  @IsOptional()
  @IsBoolean()
  featured?: boolean;
  // Note: siteId removed to prevent cross-site writes - populated from JWT context
}

// DTO for updating post (admin sends full structure with draft/published)
export class UpdatePostDto {
  @IsOptional()
  @IsString()
  publishDate?: string | null;

  @IsOptional()
  @IsString()
  archiveDate?: string | null;

  @IsOptional()
  @IsBoolean()
  restrictedAccess?: boolean;

  @Transform(
    ({ value }) => {
      if (!Array.isArray(value)) return value;
      return value.map((id) => {
        const parsed =
          typeof id === 'string' ? parseInt(id.trim(), 10) : Number(id);
        return isNaN(parsed) ? id : parsed;
      });
    },
    { toClassOnly: true }
  )
  @IsOptional()
  @IsArray({
    message: 'allowedInfluencerIds must be an array',
  })
  @ArrayUnique({ message: 'allowedInfluencerIds must contain unique values' })
  @IsInt({ each: true, message: 'Each allowedInfluencerId must be an integer' })
  allowedInfluencerIds?: number[];

  // Published content (null to clear)
  @IsOptional()
  published?: {
    title: string;
    subtitle?: string;
    author?: string;
    bannerImageAssetId?: string;
    bannerImageCrop?: CropData;
    featuredVideoAssetId?: string;
    content: string;
    featured: boolean;
    postTypes: string[];
    topics: string[];
    publishedAt: string;
  } | null;

  // Draft content (null to clear)
  @IsOptional()
  draft?: {
    title: string;
    subtitle?: string;
    author?: string;
    bannerImageAssetId?: string;
    bannerImageCrop?: CropData;
    featuredVideoAssetId?: string;
    content: string;
    featured: boolean;
    postTypes: string[];
    topics: string[];
  } | null;
}

// Attachment DTOs
export class CreateAttachmentDto {
  @IsString()
  @IsNotEmpty()
  assetId: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsString()
  displayName?: string;

  @IsOptional()
  @IsNumber()
  order?: number;
}

export class UpdateAttachmentDto {
  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsString()
  displayName?: string;

  @IsOptional()
  @IsNumber()
  order?: number;
}

// Comment DTOs
export class CreateCommentDto {
  @IsString()
  @IsNotEmpty()
  content: string;
}

export class UpdateCommentDto {
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  content?: string;

  @IsOptional()
  @IsBoolean()
  deleted?: boolean;
}
// Bookmark interfaces
export interface Bookmark {
  id: string;
  analystId: number; // Analyst ID (only analysts can bookmark)
  postId: string;
  siteId: string;
  createdAt: string; // ISO string
}

export interface BookmarkWithPost {
  id: string;
  analystId: number; // Analyst ID (only analysts can bookmark)
  postId: string;
  siteId: string;
  createdAt: string;
  post: Post | null;     // Null if deleted
  vendorName?: string;   // From site settings
  vendorSlug?: string;   // From site settings (arConnectSlug or slug)
}

// Bookmark DTOs
export class CreateBookmarkDto {
  @IsString()
  @IsNotEmpty()
  postId: string;

  @IsString()
  @IsNotEmpty()
  siteId: string;
}


// Search DTOs
export class SearchPostsDto {
  @IsString()
  @IsNotEmpty()
  keyword: string;

  @IsOptional()
  @IsString()
  siteId?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  postTypes?: string[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  topics?: string[];
}

// Post Draft DTOs
export class CreatePostDraftDto {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsOptional()
  @IsString()
  subtitle?: string;

  @IsOptional()
  @IsString()
  author?: string;

  @IsOptional()
  @IsString()
  bannerImageAssetId?: string;

  @IsOptional()
  @ValidateIf((o) => o.featuredVideoAssetId !== null)
  @IsString()
  featuredVideoAssetId?: string | null;

  @IsString()
  @MaxContentSize()
  content: string;

  @IsArray()
  @IsString({ each: true })
  postTypes: string[];

  @IsArray()
  @IsString({ each: true })
  topics: string[];

  @IsOptional()
  @IsString()
  publishDate?: string;

  @IsOptional()
  @IsString()
  archiveDate?: string;

  @IsOptional()
  @IsBoolean()
  restrictedAccess?: boolean;

  @Transform(
    ({ value }) => {
      if (!Array.isArray(value)) return value;
      return value.map((id) => {
        const parsed =
          typeof id === 'string' ? parseInt(id.trim(), 10) : Number(id);
        return isNaN(parsed) ? id : parsed;
      });
    },
    { toClassOnly: true }
  )
  @IsOptional()
  @IsArray({
    message: 'allowedInfluencerIds must be an array',
  })
  @ArrayUnique({ message: 'allowedInfluencerIds must contain unique values' })
  @IsInt({ each: true, message: 'Each allowedInfluencerId must be an integer' })
  allowedInfluencerIds?: number[];

  @IsOptional()
  @IsBoolean()
  featured?: boolean;
}

export class UpdatePostDraftDto {
  @IsOptional()
  @IsString()
  title?: string;

  @IsOptional()
  @IsString()
  subtitle?: string;

  @IsOptional()
  @IsString()
  author?: string;

  @IsOptional()
  @IsString()
  bannerImageAssetId?: string;

  @IsOptional()
  @ValidateIf((o) => o.featuredVideoAssetId !== null)
  @IsString()
  featuredVideoAssetId?: string | null;

  @IsOptional()
  @IsString()
  @MaxContentSize()
  content?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  postTypes?: string[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  topics?: string[];

  @IsOptional()
  @IsString()
  publishDate?: string;

  @IsOptional()
  @IsString()
  archiveDate?: string;

  @IsOptional()
  @IsBoolean()
  restrictedAccess?: boolean;

  @Transform(
    ({ value }) => {
      if (!Array.isArray(value)) return value;
      return value.map((id) => {
        const parsed =
          typeof id === 'string' ? parseInt(id.trim(), 10) : Number(id);
        return isNaN(parsed) ? id : parsed;
      });
    },
    { toClassOnly: true }
  )
  @IsOptional()
  @IsArray({
    message: 'allowedInfluencerIds must be an array',
  })
  @ArrayUnique({ message: 'allowedInfluencerIds must contain unique values' })
  @IsInt({ each: true, message: 'Each allowedInfluencerId must be an integer' })
  allowedInfluencerIds?: number[];

  @IsOptional()
  @IsBoolean()
  featured?: boolean;
}
